// Het scherm opbouwen uit de spelstaat. Bij elke wijziging wordt het
// speelveld opnieuw getekend. Dat mag hier, want er staat geen invoerveld op
// dat focus kan verliezen, en het houdt de code simpel.

import { avatarUrl } from './discord'
import { playersOfTeam, type GameState, type GameTeam, type WordCard } from './game'
import { isLang, t, type Lang } from './i18n'
import { PRIVACY } from './privacy'

export interface ViewActions {
  openPrivacy(): void
  resetGame(): void
  ready(): void
  joinTeam(teamId: string): void
  startGame(): void
  wordResult(correct: boolean): void
  nextTurn(): void
  backToLobby(): void
  setOption(key: string, value: unknown): void
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag)
  if (className) node.className = className
  if (text !== undefined) node.textContent = text
  return node
}

function button(label: string, className: string, onClick: () => void): HTMLButtonElement {
  const node = el('button', className, label)
  node.addEventListener('click', onClick)
  return node
}

function avatar(userId: string, hash: string | null): HTMLImageElement {
  const img = el('img')
  img.src = avatarUrl(userId, hash)
  img.alt = ''
  return img
}

/** Wat een niet-host te zien krijgt in plaats van de knop van de host. */
function waitingBar(state: GameState, key: string): HTMLElement {
  const lang = langOf(state)
  const host = state.players.find((p) => p.user_id === state.host_id)
  const wrap = el('div', 'waiting')
  wrap.append(el('span', 'waiting-main', t(lang, key)))
  if (host) {
    wrap.append(el('span', 'waiting-hint', t(lang, 'lobby.waiting.hint', { name: host.name })))
  }
  return wrap
}

export function langOf(state: GameState): Lang {
  return isLang(state.language) ? state.language : 'nl'
}

/** Teamnamen komen uit de vertaling, zodat ze met de taal meegaan. */
function teamName(lang: Lang, team: GameTeam): string {
  return t(lang, `team.${team.id}`) || team.name
}

export function phaseTitle(state: GameState): string {
  const lang = langOf(state)
  switch (state.phase) {
    case 'lobby':
      return t(lang, 'phase.lobby')
    case 'preview':
      return t(lang, state.turn?.you_describe ? 'phase.describe' : 'phase.guess')
    case 'playing':
      return t(lang, state.turn?.you_describe ? 'phase.describe' : 'phase.guess')
    case 'turn_over':
      return t(lang, 'phase.turn_over')
    case 'game_over':
      return t(lang, 'phase.game_over')
  }
}

export function playerCountLabel(state: GameState): string {
  const lang = langOf(state)
  const count = state.players.length
  return count === 1 ? t(lang, 'players.one') : t(lang, 'players.many', { count })
}

// ------------------------------------------------------------------ teams

function renderScoreboard(state: GameState, myId: string, actions: ViewActions): HTMLElement {
  const lang = langOf(state)
  const board = el('div', 'scoreboard')

  for (const team of state.teams) {
    const column = el('div', 'team')
    column.dataset.team = team.id
    if (state.turn?.team_id === team.id && state.phase === 'playing') {
      column.classList.add('active')
    }

    const head = el('div', 'team-head')
    const name = el('span', 'team-name')
    name.append(el('span', 'team-dot'), el('span', undefined, teamName(lang, team)))

    head.append(name, el('span', 'team-score', String(team.score)))
    column.append(head)

    const members = playersOfTeam(state, team.id)
    if (members.length > 0) {
      const list = el('ul', 'team-members')
      for (const player of members) {
        const item = el('li', 'chip')
        item.append(avatar(player.user_id, player.avatar_hash))
        item.append(el('span', undefined, player.name))
        if (player.user_id === state.host_id) {
          item.append(el('span', 'chip-badge', t(lang, 'badge.host')))
        }
        if (state.turn?.describer_id === player.user_id && state.phase === 'playing') {
          item.classList.add('is-describer')
        }
        list.append(item)
      }
      column.append(list)
    }

    if (state.phase === 'lobby') {
      const mine = team.member_ids.includes(myId)
      const join = button(t(lang, mine ? 'team.mine' : 'team.join'), 'join', () =>
        actions.joinTeam(team.id),
      )
      join.disabled = mine
      column.append(join)
    }

    board.append(column)
  }

  return board
}

/**
 * Het touw. Een gedeelde balk die in het midden begint: het eerste team
 * trekt naar links, het tweede naar rechts. De vulling loopt vanaf het
 * midden naar de kant die aan de winnende hand is.
 */
function renderTug(state: GameState): HTMLElement {
  const lang = langOf(state)
  const wrap = el('div', 'tug')

  const [first, second] = state.teams
  const leader = state.rope > 0 ? first : state.rope < 0 ? second : undefined
  const fraction = Math.min(1, Math.abs(state.rope) / state.rope_size)
  const half = fraction * 50

  const track = el('div', 'tug-track')
  const fill = el('div', 'tug-fill')
  // Positief trekt naar links, want daar staat de kaart van het eerste team.
  fill.style.left = state.rope > 0 ? `${50 - half}%` : '50%'
  fill.style.width = `${half}%`
  if (leader) fill.dataset.team = leader.id

  const knot = el('div', 'tug-knot')
  knot.style.left = `${state.rope > 0 ? 50 - half : 50 + half}%`
  if (leader) knot.dataset.team = leader.id
  track.append(el('div', 'tug-centre'), fill, knot)

  const labels = el('div', 'tug-labels')
  labels.append(el('span', 'tug-side', first ? teamName(lang, first) : ''))
  labels.append(
    el(
      'span',
      'tug-state',
      state.rope === 0
        ? t(lang, 'tug.centre')
        : `${Math.abs(state.rope)} / ${state.rope_size}`,
    ),
  )
  labels.append(el('span', 'tug-side end', second ? teamName(lang, second) : ''))

  wrap.append(track, labels)

  if (state.phase !== 'lobby') {
    wrap.append(
      el(
        'span',
        'tug-turns',
        t(lang, 'tug.turns', {
          done: Math.min(Math.ceil(state.turns_played / 2), Math.ceil(state.max_turns / 2)),
          total: Math.ceil(state.max_turns / 2),
        }),
      ),
    )
  }

  return wrap
}

// --------------------------------------------------------------- privacy

/** De privacyverklaring. Zelfde opbouw in het spel en op de losse pagina. */
export function renderPrivacy(
  root: HTMLElement,
  lang: Lang,
  onClose?: () => void,
): void {
  const doc = PRIVACY[lang]
  root.replaceChildren()

  const blad = el('article', 'privacy')
  blad.append(el('h2', undefined, doc.title))
  blad.append(el('span', 'privacy-updated', doc.updated))

  for (const alinea of doc.intro) {
    blad.append(el('p', 'privacy-intro', alinea))
  }

  for (const sectie of doc.sections) {
    blad.append(el('h3', undefined, sectie.title))
    const lijst = el('ul', 'privacy-list')
    for (const regel of sectie.body) {
      lijst.append(el('li', undefined, regel))
    }
    blad.append(lijst)
  }

  const link = document.createElement('a')
  link.href = 'https://discord.com/privacy'
  link.target = '_blank'
  link.rel = 'noopener noreferrer'
  link.className = 'privacy-link'
  link.textContent = t(lang, 'privacy.discord')
  blad.append(link)

  if (onClose) {
    blad.append(button(t(lang, 'privacy.close'), 'primary big', onClose))
  }

  root.append(blad)
}

// ---------------------------------------------------------------- splash

/**
 * Het laadscherm. Blijft staan tot de eerste spelstaat binnen is.
 *
 * De tekst zegt bij welke stap we zijn. Blijft hij hangen, dan zie je meteen
 * of het bij Discord, bij het inloggen of bij de room misgaat, in plaats van
 * dat je naar een leeg scherm kijkt.
 */
export function renderSplash(root: HTMLElement, statusKey: string, lang: Lang = 'nl'): void {
  root.replaceChildren()

  const splash = el('div', 'splash')
  splash.append(el('span', 'brand-name', t(lang, 'brand')))

  const card = el('div', 'bigcard splash-card')
  card.append(el('span', 'bigcard-number', '30'))
  splash.append(card)

  splash.append(el('span', 'splash-status', t(lang, statusKey)))

  const dots = el('div', 'splash-dots')
  for (let i = 0; i < 3; i += 1) dots.append(el('span'))
  splash.append(dots)

  root.append(splash)
}

// ------------------------------------------------------------- ronde-kop

/** De kop tijdens een beurt: welke ronde en wie er omschrijft. */
function renderTurnHeader(state: GameState): HTMLElement {
  const lang = langOf(state)
  const turn = state.turn!
  const team = state.teams.find((tm) => tm.id === turn.team_id)
  const describer = state.players.find((p) => p.user_id === turn.describer_id)

  const bar = el('div', 'roundbar')

  const left = el('div', 'roundbar-side')
  const round = Math.max(1, Math.ceil(state.turns_played / Math.max(1, state.teams.length)))
  left.append(el('span', 'roundbar-label', t(lang, 'round.label', { n: round })))
  const teamLine = el('span', 'roundbar-value')
  if (team) {
    const dot = el('span', 'team-dot')
    dot.dataset.team = team.id
    teamLine.append(dot, el('span', undefined, teamName(lang, team)))
  }
  left.append(teamLine)

  const right = el('div', 'roundbar-side end')
  right.append(el('span', 'roundbar-label', t(lang, 'round.describer')))
  const who = el('span', 'roundbar-value')
  if (describer) {
    who.append(avatar(describer.user_id, describer.avatar_hash))
    who.append(el('span', undefined, describer.name))
  }
  right.append(who)

  bar.append(left, right)
  return bar
}

// ------------------------------------------------------------------ intro

/** Scherm 1 van de beurt: wie omschrijft er, met de kaart als blikvanger. */
function renderIntro(state: GameState): HTMLElement {
  const lang = langOf(state)
  const turn = state.turn!
  const stage = el('div', 'stage centered intro')

  const card = el('div', 'bigcard')
  card.append(el('span', 'bigcard-number', String(state.turn_seconds)))
  stage.append(card)

  stage.append(
    el(
      'h2',
      'intro-title',
      turn.you_describe
        ? t(lang, 'intro.you')
        : t(lang, 'intro.other', { name: turn.describer_name }),
    ),
  )
  stage.append(
    el('p', 'intro-hint', t(lang, turn.you_describe ? 'intro.you.hint' : 'intro.other.hint')),
  )
  return stage
}

// ---------------------------------------------------------------- kaart

/** Streepjes in de vorm van het woord, zodat je ziet hoe lang het is. */
function maskedWord(pattern: number[]): HTMLElement {
  const wrap = el('span', 'wordcard-mask')
  for (const length of pattern) {
    const group = el('span', 'mask-group')
    for (let i = 0; i < length; i += 1) {
      group.append(el('span', 'mask-dash'))
    }
    wrap.append(group)
  }
  return wrap
}

function wordList(cards: WordCard[], options: { showProgress?: boolean } = {}): HTMLElement {
  const list = el('ul', 'wordlist')
  cards.forEach((card, index) => {
    const item = el('li', 'wordcard')
    if (card.current) item.classList.add('current')

    // Groen als het binnen is, rood als er gepast is. Geldt voor iedereen,
    // ook als het woord zelf nog verborgen blijft.
    if (card.result === 'correct') item.classList.add('is-correct')
    if (card.result === 'pass') item.classList.add('is-missed')

    // De omschrijver heeft ze al gehad, bij hem mogen ze wegzakken.
    if (options.showProgress && card.result) item.classList.add('handled')

    if (card.word) {
      item.append(el('span', 'wordcard-text', card.word))
      // Bij de raders klapt het woord tevoorschijn, dat mag opvallen.
      if (!options.showProgress && card.result === 'correct') item.classList.add('revealed')
    } else {
      item.append(maskedWord(card.pattern))
    }

    item.style.animationDelay = `${index * 55}ms`
    list.append(item)
  })
  return list
}

/** Scherm 2: de omschrijver leest zijn kaart door en start zelf de klok. */
function renderPreview(state: GameState, actions: ViewActions): HTMLElement {
  const lang = langOf(state)
  const turn = state.turn!
  const stage = el('div', 'stage centered')

  if (turn.you_describe) {
    stage.append(el('span', 'turn-label', t(lang, 'preview.read')))
    stage.append(wordList(turn.cards))
    stage.append(button(t(lang, 'preview.ready'), 'primary ready', actions.ready))
  } else {
    const card = el('div', 'bigcard waiting-card')
    card.append(el('span', 'bigcard-number', String(state.turn_seconds)))
    stage.append(card)
    stage.append(el('p', 'intro-hint', t(lang, 'preview.waiting', { name: turn.describer_name })))
  }

  return stage
}

// -------------------------------------------------------------- instellingen

function segmented(
  label: string,
  choices: { value: string; label: string }[],
  current: string,
  onPick: (value: string) => void,
): HTMLElement {
  const row = el('div', 'setting')
  row.append(el('span', 'setting-label', label))
  const group = el('div', 'segmented')
  for (const choice of choices) {
    const node = button(choice.label, 'segment', () => onPick(choice.value))
    if (choice.value === current) node.classList.add('on')
    group.append(node)
  }
  row.append(group)
  return row
}

function renderSettings(state: GameState, actions: ViewActions): HTMLElement {
  const lang = langOf(state)
  const box = el('div', 'settings')

  box.append(
    segmented(
      t(lang, 'setting.language'),
      [
        { value: 'nl', label: 'Nederlands' },
        { value: 'en', label: 'English' },
      ],
      state.language,
      (value) => actions.setOption('language', value),
    ),
  )

  const scoring = segmented(
    t(lang, 'setting.scoring'),
    [
      { value: 'tug', label: t(lang, 'scoring.tug') },
      { value: 'points', label: t(lang, 'scoring.points') },
    ],
    state.scoring,
    (value) => actions.setOption('scoring', value),
  )
  scoring.append(
    el(
      'span',
      'setting-hint',
      state.scoring === 'tug'
        ? t(lang, 'scoring.tug.hint', { rounds: Math.ceil(state.max_turns / 2) })
        : t(lang, 'scoring.points.hint', { target: state.target_score }),
    ),
  )
  box.append(scoring)

  // Naast de resetknop, zoals afgesproken.
  const meeluisteren = segmented(
    t(lang, 'setting.listening'),
    [
      { value: 'aan', label: t(lang, 'listening.on') },
      { value: 'uit', label: t(lang, 'listening.off') },
    ],
    state.listening ? 'aan' : 'uit',
    (value) => actions.setOption('listening', value === 'aan'),
  )

  // Na het uitzetten kan het een minuut lang niet weer aan. Die knop grijs
  // laten zonder uitleg is vervelend, dus er komt een aftelling bij.
  if (!state.listening && state.listening_ready_at > 0) {
    const aan = meeluisteren.querySelector<HTMLButtonElement>('.segment')
    if (aan) {
      aan.disabled = true
      aan.id = 'listen-cooldown'
      aan.dataset.readyAt = String(state.listening_ready_at)
      aan.dataset.label = t(lang, 'listening.on')
    }
  }

  meeluisteren.append(
    el(
      'span',
      'setting-hint',
      !state.bot_online
        ? t(lang, 'listening.offline')
        : state.listening
          ? t(lang, 'listening.hint')
          : t(lang, 'listening.hint.off'),
    ),
  )
  box.append(renderReset(state, actions))
  box.append(meeluisteren)

  return box
}

/**
 * Resetten in twee tikken. Een knop die meteen een lopende pot wist is een
 * valstrik: dit gaat om een stand waar iedereen tien minuten aan gewerkt
 * heeft.
 */
function renderReset(state: GameState, actions: ViewActions): HTMLElement {
  const lang = langOf(state)
  const row = el('div', 'setting')
  row.append(el('span', 'setting-label', t(lang, 'setting.game')))

  let timer: ReturnType<typeof setTimeout> | undefined
  const btn = button(t(lang, 'game.reset'), 'danger wide-button', () => {
    if (btn.dataset.armed === 'true') {
      if (timer) clearTimeout(timer)
      actions.resetGame()
      return
    }
    btn.dataset.armed = 'true'
    btn.textContent = t(lang, 'game.reset.confirm')
    timer = setTimeout(() => {
      delete btn.dataset.armed
      btn.textContent = t(lang, 'game.reset')
    }, 4000)
  })

  row.append(btn)
  row.append(el('span', 'setting-hint', t(lang, 'game.reset.hint')))
  return row
}

// ------------------------------------------------------------------- lobby

function renderBench(state: GameState): HTMLElement | null {
  // Wie nog geen team heeft was anders onzichtbaar, en dan lijkt de lobby
  // leeg terwijl je vrienden er gewoon in zitten.
  const waiting = state.players.filter((p) => !p.team_id)
  if (waiting.length === 0) return null

  const bench = el('div', 'bench')
  bench.append(el('span', 'bench-label', t(langOf(state), 'bench.label')))
  const list = el('ul', 'bench-list')
  for (const player of waiting) {
    const item = el('li', 'chip')
    item.append(avatar(player.user_id, player.avatar_hash))
    item.append(el('span', undefined, player.name))
    if (player.user_id === state.host_id) {
      item.append(el('span', 'chip-badge', t(langOf(state), 'badge.host')))
    }
    list.append(item)
  }
  bench.append(list)
  return bench
}

function renderLobby(state: GameState, actions: ViewActions): HTMLElement {
  const lang = langOf(state)
  const stage = el('div', 'stage')

  const bench = renderBench(state)
  if (bench) stage.append(bench)

  const teamsWithPlayers = state.teams.filter((team) => team.member_ids.length > 0)
  if (teamsWithPlayers.length === 1) {
    stage.append(el('div', 'note', t(lang, 'lobby.solo')))
  }

  if (!state.you_are_host) {
    // Geen instellingen en geen startknop: die zijn toch van de host, en een
    // knop die je wel ziet maar niet mag gebruiken is alleen maar vervelend.
    stage.append(waitingBar(state, 'lobby.waiting'))
    stage.append(button(t(lang, 'privacy.open'), 'linkish', actions.openPrivacy))
    return stage
  }

  stage.append(renderSettings(state, actions))

  const start = button(t(lang, 'lobby.start'), 'primary big', actions.startGame)
  start.disabled = teamsWithPlayers.length === 0
  stage.append(start)

  if (teamsWithPlayers.length === 0) {
    stage.append(el('span', 'setting-hint centered-text', t(lang, 'lobby.pick')))
  }

  stage.append(button(t(lang, 'privacy.open'), 'linkish', actions.openPrivacy))

  return stage
}

/**
 * De balk die laat zien of er meegeluisterd wordt.
 *
 * Bewust gebaseerd op wat er echt gebeurt en niet op de instelling: hij is
 * groen als de microfoons daadwerkelijk open staan. Buiten een ronde staat
 * er dat ze dicht zijn.
 */
function renderListeningBar(state: GameState): HTMLElement | null {
  if (!state.listening || !state.bot_online) return null

  const lang = langOf(state)
  const bar = el('div', 'micbar')
  bar.dataset.live = String(state.listening_now)
  bar.append(el('span', 'micdot'))

  let tekst = t(lang, 'banner.idle')
  if (state.listening_now) {
    const turn = state.turn
    tekst = t(
      lang,
      turn && turn.you_describe ? 'banner.live.describe' : 'banner.live.guess',
    )
  }
  bar.append(el('span', undefined, tekst))
  return bar
}

// ------------------------------------------------------------------ spelen

function renderPlaying(state: GameState, actions: ViewActions): HTMLElement {
  const lang = langOf(state)
  const turn = state.turn!
  const stage = el('div', 'stage playfield')

  const clock = el('div', 'clock')
  const big = el('div', 'clock-number', '')
  big.id = 'timer-text'
  const track = el('div', 'timer-track')
  const bar = el('div', 'timer-bar')
  bar.id = 'timer-bar'
  track.append(bar)
  clock.append(big, track)
  clock.append(
    el('span', 'clock-hint', t(lang, 'play.guessed', { done: turn.correct, total: turn.words_total })),
  )

  const right = el('div', 'playfield-right')
  right.append(wordList(turn.cards, { showProgress: turn.you_describe }))

  if (turn.you_describe) {
    const buttons = el('div', 'word-actions')
    buttons.append(button(t(lang, 'play.good'), 'good', () => actions.wordResult(true)))
    buttons.append(button(t(lang, 'play.pass'), 'pass', () => actions.wordResult(false)))
    right.append(buttons)
  } else {
    // Je eigen team mag roepen, de tegenpartij hoort stil te zijn.
    right.append(el('span', 'watch-hint', t(lang, turn.you_guess ? 'play.shout' : 'play.watch')))
  }

  stage.append(clock, right)
  return stage
}

function renderTurnOver(state: GameState, actions: ViewActions): HTMLElement {
  const lang = langOf(state)
  const turn = state.turn
  const stage = el('div', 'stage centered')
  const team = state.teams.find((tm) => tm.id === turn?.team_id)

  stage.append(el('p', 'turn-label', t(lang, `end.${state.last_end_reason ?? 'time_up'}`)))

  if (turn && team) {
    const tally = el('div', 'tally')
    if (state.scoring === 'tug' && turn.pulled != null) {
      tally.append(el('span', 'tally-score', `+${turn.pulled}`))
      tally.append(
        el('span', 'tally-label', t(lang, 'over.pulled', { pulled: turn.pulled, team: teamName(lang, team) })),
      )
    } else {
      tally.append(el('span', 'tally-score', `+${turn.correct}`))
      tally.append(el('span', 'tally-label', t(lang, 'over.points', { team: teamName(lang, team) })))
    }
    stage.append(tally)
  }

  stage.append(button(t(lang, 'over.next'), 'primary big', actions.nextTurn))
  return stage
}

function renderGameOver(state: GameState, actions: ViewActions): HTMLElement {
  const lang = langOf(state)
  const stage = el('div', 'stage centered')
  const winner = state.teams.find((tm) => tm.id === state.winner_team_id)

  const headline = state.is_draw
    ? t(lang, 'over.draw')
    : winner
      ? t(lang, 'over.winner', { team: teamName(lang, winner) })
      : t(lang, 'over.done')
  stage.append(el('h2', 'winner', headline))
  stage.append(
    el('p', 'muted', state.teams.map((tm) => `${teamName(lang, tm)} ${tm.score}`).join('   ')),
  )
  if (state.you_are_host) {
    stage.append(button(t(lang, 'over.lobby'), 'primary big', actions.backToLobby))
  } else {
    stage.append(waitingBar(state, 'over.waiting'))
  }
  return stage
}

export function renderGame(
  root: HTMLElement,
  state: GameState,
  myId: string,
  actions: ViewActions,
  showIntro = false,
): void {
  root.replaceChildren()

  const inRound = state.phase === 'preview' || state.phase === 'playing'

  if (inRound) {
    // Tijdens een beurt telt alleen de kaart en de klok. Het scorebord komt
    // erna weer terug.
    root.append(renderTurnHeader(state))
  } else {
    root.append(renderScoreboard(state, myId, actions))

    // De balk verschijnt pas als de beurt voorbij is. Tijdens het raden
    // beweegt hij toch niet, want de stand wordt aan het eind van de beurt
    // verrekend.
    if (state.scoring === 'tug' && state.turns_played > 0) {
      root.append(renderTug(state))
    }
  }

  switch (state.phase) {
    case 'lobby':
      root.append(renderLobby(state, actions))
      break
    case 'preview':
      root.append(showIntro ? renderIntro(state) : renderPreview(state, actions))
      break
    case 'playing':
      root.append(renderPlaying(state, actions))
      break
    case 'turn_over':
      root.append(renderTurnOver(state, actions))
      break
    case 'game_over':
      root.append(renderGameOver(state, actions))
      break
  }

  // Onderaan, zoals een statusbalk hoort. Alleen tijdens een beurt, want in
  // de lobby zegt de instelling het al.
  if (inRound) {
    const bar = renderListeningBar(state)
    if (bar) root.append(bar)
  }
}
